import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { env } from "../../lib/env.ts";
import { mockInbox, mockJoinTables } from "../../mocks/data.ts";
import { analyticsClient } from "../analytics/analytics-client.ts";
import { track } from "../analytics/track.ts";
import {
  type CheckInSummary,
  CUSTOMER_DEMO_MENU_ITEM_ID,
  CUSTOMER_DEMO_RECIPIENT_PARTY_ID,
  CUSTOMER_DEMO_TABLE_ID,
  customerBackendApi,
} from "../api/backend.ts";
import { retryWithBackoff } from "../connectivity/retry.ts";
import { type MessageKey, t } from "../i18n/index.ts";
import {
  applyInboxAction,
  type InboxAction,
  type InboxItem,
  pendingCount,
} from "../inbox/inbox.ts";
import {
  addItem,
  type CartItem,
  type CartItemInput,
  cartCount,
  cartTotalMinor,
  removeItem,
  setQuantity,
} from "../order/cart.ts";
import type { TonightMode } from "../tonight/mode.ts";

export const CONSENT_VERSION = "jod-consent-v1";
export const CONSENT_STORAGE_KEY = "jod.customer.consent";

/** Order lifecycle for the live status timeline (schema OrderStatus mirror). */
export type LiveOrderStatus =
  | "pending"
  | "accepted"
  | "preparing"
  | "ready"
  | "served"
  | "cancelled"
  | "rejected";

export const ORDER_STEPS: { id: LiveOrderStatus; labelKey: MessageKey }[] = [
  { id: "pending", labelKey: "order.step.pending" },
  { id: "accepted", labelKey: "order.step.accepted" },
  { id: "preparing", labelKey: "order.step.preparing" },
  { id: "ready", labelKey: "order.step.ready" },
  { id: "served", labelKey: "order.step.served" },
];

export const TERMINAL_ORDER_STATUSES: LiveOrderStatus[] = ["served", "cancelled", "rejected"];

export interface LiveOrder {
  id: string;
  lines: CartItem[];
  status: LiveOrderStatus;
  submittedAt: string;
  staffNote?: string;
}

export interface ConsentState {
  accepted: boolean;
  version: string;
  acceptedAt?: string;
}

export interface SocialSettings {
  allowGifts: boolean;
  allowContact: boolean;
}

export type SocialRequestKind = "gift" | "contact" | "report";
export type SocialRequestStatus =
  | "pending_acceptance"
  | "accepted"
  | "fulfilled"
  | "declined"
  | "expired"
  | "staff_rejected"
  | "blocked"
  | "reported";

export interface SocialRequest {
  id: string;
  kind: SocialRequestKind;
  toTable: string;
  subject: string;
  message?: string;
  requestedContact?: boolean;
  status: SocialRequestStatus;
  createdAt: string;
}

export interface SafetyReport {
  id: string;
  tableCode: string;
  reason: string;
  createdAt: string;
}

export interface SocialGuardResult {
  ok: boolean;
  reason?: string;
}

export interface ToastMessage {
  id: number;
  text: string;
}

interface GiftRequestInput {
  toTable: string;
  subject: string;
  message?: string;
  requestedContact: boolean;
  menuItemId?: string;
  /** Real backend party id of the recipient table (from the Around feed). */
  targetPartyId?: string;
}

interface ContactRequestInput {
  toTable: string;
  subject: string;
  message?: string;
  /** Real backend party id of the recipient table (from the Around feed). */
  targetPartyId?: string;
  /** Optional preset key, e.g. "ig" / "chat", forwarded to the backend. */
  messagePresetKey?: string;
}

interface SessionValue {
  consent: ConsentState;
  acceptConsent: () => void;

  /** Tonight social mode — defaults to `private` (invisible) per CLAUDE.md. */
  mode: TonightMode;
  setMode: (mode: TonightMode) => void;
  tableCode: string;
  /**
   * Backend check-in summary (party id, check-in id, venue session id) once the
   * caller has joined a table in backend mode. `null` in mock mode / pre-join.
   */
  checkIn: CheckInSummary | null;
  joinTable: (code: string, options?: { tableId?: string; displayName?: string }) => void;

  cart: CartItem[];
  cartCount: number;
  cartTotalMinor: number;
  addToCart: (item: CartItemInput, quantity?: number) => void;
  updateQty: (id: string, quantity: number) => void;
  removeFromCart: (id: string) => void;

  order: LiveOrder | null;
  submitOrder: () => void;
  advanceOrderStatus: (status: LiveOrderStatus, staffNote?: string) => void;
  /**
   * Reconcile the live order status from a backend `GET /orders/me` poll. Only
   * used in backend mode; mock mode keeps the simulated auto-advance timer.
   */
  syncOrderStatus: (status: LiveOrderStatus) => void;

  settings: SocialSettings;
  setAllowGifts: (allow: boolean) => void;
  setAllowContact: (allow: boolean) => void;
  canStartSocial: (input: {
    toTable: string;
    kind: "gift" | "contact";
    requestedContact?: boolean;
  }) => SocialGuardResult;
  sentSocial: SocialRequest[];
  sendGiftRequest: (input: GiftRequestInput) => SocialGuardResult;
  sendContactRequest: (input: ContactRequestInput) => SocialGuardResult;
  blockedTables: string[];
  reports: SafetyReport[];
  blockTable: (tableCode: string, targetPartyId?: string) => void;
  /** Mock-mode-only local unblock (FR-CUS-124). Backend mode manages its own
   * block list via `customerBackendApi.listBlocks` / `unblock` — see
   * `routes/blocked.tsx`. */
  unblockTable: (tableCode: string) => void;
  reportTable: (tableCode: string, reason?: string, targetPartyId?: string) => void;
  leaveVenue: () => SocialGuardResult;

  inbox: InboxItem[];
  pendingInbox: number;
  respondInbox: (id: string, action: InboxAction) => void;

  /** Whether backend-wired flows are active (false in explicit mock mode). */
  backendMode: boolean;
  /**
   * Backend gift-inbox response. `accept`/`reject` hit the gift endpoints;
   * `block` additionally posts a block against the sender party. Returns a
   * promise so callers can refetch the inbox query on success.
   */
  respondGift: (
    giftId: string,
    action: "accept" | "reject" | "block",
    senderPartyId?: string,
  ) => Promise<void>;
  /** Backend contact-request inbox response (accept / reject / block). */
  respondContact: (id: string, action: "accept" | "reject" | "block") => Promise<void>;

  toasts: ToastMessage[];
  toast: (text: string) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function readStoredConsent(): ConsentState {
  if (typeof window === "undefined") return { accepted: false, version: CONSENT_VERSION };
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return { accepted: false, version: CONSENT_VERSION };
    const parsed = JSON.parse(stored) as ConsentState;
    return parsed.version === CONSENT_VERSION && parsed.accepted
      ? parsed
      : { accepted: false, version: CONSENT_VERSION };
  } catch {
    return { accepted: false, version: CONSENT_VERSION };
  }
}

const TONIGHT_MODE_STORAGE_KEY = "jod.customer.tonight-mode";
const TONIGHT_MODES: TonightMode[] = ["private", "soft_open", "lets_cheers", "with_friends"];

function readStoredMode(): TonightMode {
  if (typeof window === "undefined") return "private";
  try {
    const stored = window.sessionStorage.getItem(TONIGHT_MODE_STORAGE_KEY);
    return stored && TONIGHT_MODES.includes(stored as TonightMode)
      ? (stored as TonightMode)
      : "private";
  } catch {
    return "private";
  }
}

function nowIso() {
  return new Date().toISOString();
}

function sequenceId(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function isOrderTerminal(status: LiveOrderStatus): boolean {
  return TERMINAL_ORDER_STATUSES.includes(status);
}

/** Backend mode is active whenever the typed facade was constructed. */
const BACKEND_MODE = !env.apiMock && customerBackendApi !== null;

function normalizeTonightMode(value: string): TonightMode {
  return TONIGHT_MODES.includes(value as TonightMode) ? (value as TonightMode) : "private";
}

function reportError(error: unknown, fallback: string): string {
  return error instanceof Error ? `${fallback}: ${error.message}` : fallback;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<ConsentState>(() => readStoredConsent());
  // Tonight mode is per-night state: survive in-venue reloads via
  // sessionStorage, but reset (back to default-private) for a fresh session.
  const [mode, setModeState] = useState<TonightMode>(() => readStoredMode());
  // Persist the fast-path cache immediately; the backend (when present) is
  // authoritative and reconciles the stored value on its response.
  const cacheMode = useCallback((next: TonightMode) => {
    setModeState(next);
    try {
      window.sessionStorage.setItem(TONIGHT_MODE_STORAGE_KEY, next);
    } catch {
      // Session storage is best-effort in LIFF webviews and test sandboxes.
    }
  }, []);
  const [tableCode, setTableCode] = useState("B4");
  const [checkIn, setCheckIn] = useState<CheckInSummary | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [order, setOrder] = useState<LiveOrder | null>(null);
  const [settings, setSettings] = useState<SocialSettings>({
    allowGifts: true,
    allowContact: true,
  });
  const [sentSocial, setSentSocial] = useState<SocialRequest[]>([]);
  const [blockedTables, setBlockedTables] = useState<string[]>([]);
  const [reports, setReports] = useState<SafetyReport[]>([]);
  // Backend mode sources the inbox from the live gift/contact queries, so the
  // fabricated demo inbox is mock-mode only.
  const [inbox, setInbox] = useState<InboxItem[]>(BACKEND_MODE ? [] : mockInbox);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastSeq = useRef(0);

  const toast = useCallback((text: string) => {
    const id = toastSeq.current++;
    setToasts((t) => [...t, { id, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);

  /**
   * Reconcile local fast-path state from an authoritative backend check-in
   * summary: tonight mode, the do-not-contact toggle (inverse of the me-tab
   * "allow contact" switch), and the displayed table code.
   */
  const applyCheckIn = useCallback(
    (summary: CheckInSummary) => {
      setCheckIn(summary);
      cacheMode(normalizeTonightMode(summary.tonightMode));
      setTableCode(summary.tableCode);
      setSettings((current) => ({ ...current, allowContact: !summary.notAcceptingContact }));
    },
    [cacheMode],
  );

  const acceptConsent = useCallback(() => {
    const next = { accepted: true, version: CONSENT_VERSION, acceptedAt: nowIso() };
    setConsent(next);
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Local storage is best-effort in LIFF webviews and test sandboxes.
    }
    // Unlocks non-essential client analytics (FR-ANALYTICS-003) — flushes
    // anything buffered while consent was unresolved.
    analyticsClient.setConsent(true);
  }, []);

  const joinTable = useCallback(
    (code: string, options?: { tableId?: string; displayName?: string }) => {
      const normalized = code.toUpperCase();
      const table = mockJoinTables.find((candidate) => candidate.code === normalized);
      setTableCode(table?.code ?? normalized);
      if (BACKEND_MODE && customerBackendApi && options?.tableId) {
        void customerBackendApi
          .createCheckIn(
            {
              tableId: options.tableId,
              displayName: options.displayName ?? null,
              // The current cached mode is sent so a re-join preserves the
              // guest's chosen visibility; the response stays authoritative.
              tonightMode: mode,
            },
            sequenceId("customer-checkin"),
          )
          .then(applyCheckIn)
          .catch((error) => toast(reportError(error, t("session.checkInFailed"))));
      }
    },
    [applyCheckIn, mode, toast],
  );

  const addToCart = useCallback((item: CartItemInput, quantity = 1) => {
    setCart((c) => addItem(c, item, quantity));
  }, []);
  const updateQty = useCallback((id: string, quantity: number) => {
    setCart((c) => setQuantity(c, id, quantity));
  }, []);
  const removeFromCart = useCallback((id: string) => {
    setCart((c) => removeItem(c, id));
  }, []);

  const submitOrder = useCallback(() => {
    // The backend call must live OUTSIDE a setState updater: React invokes
    // updaters twice in StrictMode, which double-submitted orders with two
    // different idempotency keys.
    if (cart.length === 0) return;
    const lines = cart;
    setOrder({
      id: sequenceId("order"),
      lines,
      status: "pending",
      submittedAt: nowIso(),
    });
    setCart([]);
    if (!env.apiMock && customerBackendApi) {
      // Money-moving guard (FR-OFF-009/005): the same Idempotency-Key is
      // reused on every retry (it's generated once, above the retry loop),
      // so a retried submit never creates a second Order. Only network-type
      // failures are retried — a 4xx (e.g. table closed) fails immediately.
      const backend = customerBackendApi;
      const idempotencyKey = sequenceId("customer-order");
      void retryWithBackoff(
        () =>
          backend.createOrder(
            {
              tableId: checkIn?.tableId ?? CUSTOMER_DEMO_TABLE_ID,
              lines: lines.map((line) => ({
                menuItemId: line.id,
                quantity: line.quantity,
                note: null,
              })),
              note: null,
            },
            idempotencyKey,
          ),
        {
          onRetry: (_error, attempt) => {
            if (attempt === 1) toast(t("session.orderRetrying"));
          },
        },
      ).catch((error) => {
        // Roll back the optimistic order so the live screen never shows
        // a "pending" order the backend rejected; restore the cart so
        // the customer can retry.
        setOrder(null);
        setCart(lines);
        toast(
          error instanceof Error
            ? `Backend order failed: ${error.message}`
            : "Backend order failed",
        );
      });
    }
  }, [cart, checkIn, toast]);

  const advanceOrderStatus = useCallback((status: LiveOrderStatus, staffNote?: string) => {
    setOrder((current) => (current ? { ...current, status, staffNote } : current));
  }, []);

  const syncOrderStatus = useCallback((status: LiveOrderStatus) => {
    setOrder((current) =>
      current && current.status !== status ? { ...current, status } : current,
    );
  }, []);

  const setMode = useCallback(
    (next: TonightMode) => {
      cacheMode(next);
      track("privacy_mode_selected", {
        mode: next,
        not_accepting_contact: !settings.allowContact,
        is_default: next === "private",
      });
      if (BACKEND_MODE && customerBackendApi && checkIn) {
        void customerBackendApi
          .updateTonightMode({ tonightMode: next })
          .then(applyCheckIn)
          .catch((error) => toast(reportError(error, t("session.modeChangeFailed"))));
      }
    },
    [applyCheckIn, cacheMode, checkIn, settings.allowContact, toast],
  );

  const setAllowGifts = useCallback((allow: boolean) => {
    setSettings((current) => ({ ...current, allowGifts: allow }));
  }, []);

  const setAllowContact = useCallback(
    (allow: boolean) => {
      setSettings((current) => ({ ...current, allowContact: allow }));
      // The me-tab "allow contact" toggle is the inverse of the backend's
      // notAcceptingContact flag, persisted on the active check-in.
      if (BACKEND_MODE && customerBackendApi && checkIn) {
        void customerBackendApi
          .updateTonightMode({ tonightMode: mode, notAcceptingContact: !allow })
          .then(applyCheckIn)
          .catch((error) => toast(reportError(error, t("session.settingsSaveFailed"))));
      }
    },
    [applyCheckIn, checkIn, mode, toast],
  );

  const canStartSocial = useCallback<SessionValue["canStartSocial"]>(
    ({ toTable, kind, requestedContact }) => {
      if (mode === "private") {
        return { ok: false, reason: t("guard.privateMode") };
      }
      if (blockedTables.includes(toTable)) {
        return { ok: false, reason: t("guard.blockedTable", { table: toTable }) };
      }
      if (kind === "contact" && !settings.allowContact) {
        return { ok: false, reason: t("guard.contactOff") };
      }
      if (requestedContact && !settings.allowContact) {
        return { ok: false, reason: t("guard.contactOffIg") };
      }
      const duplicate = sentSocial.some(
        (request) =>
          request.toTable === toTable &&
          request.kind === kind &&
          ["pending_acceptance", "accepted"].includes(request.status),
      );
      if (duplicate) {
        return { ok: false, reason: t("guard.duplicateRequest", { table: toTable }) };
      }
      return { ok: true };
    },
    [blockedTables, mode, sentSocial, settings.allowContact],
  );

  const sendGiftRequest = useCallback<SessionValue["sendGiftRequest"]>(
    (input) => {
      const guard = canStartSocial({
        toTable: input.toTable,
        kind: "gift",
        requestedContact: input.requestedContact,
      });
      if (!guard.ok) return guard;
      setSentSocial((requests) => [
        {
          id: sequenceId("gift"),
          kind: "gift",
          toTable: input.toTable,
          subject: input.subject,
          message: input.message,
          requestedContact: input.requestedContact,
          status: "pending_acceptance",
          createdAt: nowIso(),
        },
        ...requests,
      ]);
      if (BACKEND_MODE && customerBackendApi) {
        // Money-moving guard (FR-OFF-009/005) — see submitOrder for the same
        // reused-Idempotency-Key-across-retries reasoning.
        const backend = customerBackendApi;
        const idempotencyKey = sequenceId("customer-gift");
        void retryWithBackoff(
          () =>
            backend.sendGift(
              {
                targetType: "party",
                recipientPartyId: input.targetPartyId ?? CUSTOMER_DEMO_RECIPIENT_PARTY_ID,
                recipientCheckInId: null,
                menuItemId: input.menuItemId ?? CUSTOMER_DEMO_MENU_ITEM_ID,
                message: input.message ?? null,
                requestedContact: input.requestedContact,
              },
              idempotencyKey,
            ),
          {
            onRetry: (_error, attempt) => {
              if (attempt === 1) toast(t("session.giftRetrying"));
            },
          },
        ).catch((error) => toast(reportError(error, t("session.giftSendFailed"))));
      }
      return { ok: true };
    },
    [canStartSocial, toast],
  );

  const sendContactRequest = useCallback<SessionValue["sendContactRequest"]>(
    (input) => {
      const guard = canStartSocial({ toTable: input.toTable, kind: "contact" });
      if (!guard.ok) return guard;
      setSentSocial((requests) => [
        {
          id: sequenceId("contact"),
          kind: "contact",
          toTable: input.toTable,
          subject: input.subject,
          message: input.message,
          status: "pending_acceptance",
          createdAt: nowIso(),
        },
        ...requests,
      ]);
      if (BACKEND_MODE && customerBackendApi && input.targetPartyId) {
        void customerBackendApi
          .createContactRequest({
            targetType: "party",
            targetPartyId: input.targetPartyId,
            messagePresetKey: input.messagePresetKey ?? null,
          })
          .catch((error) => toast(reportError(error, t("session.contactSendFailed"))));
      }
      return { ok: true };
    },
    [canStartSocial, toast],
  );

  const blockTable = useCallback(
    (targetTable: string, targetPartyId?: string) => {
      setBlockedTables((current) =>
        current.includes(targetTable) ? current : [...current, targetTable].sort(),
      );
      setSentSocial((requests) =>
        requests.map((request) =>
          request.toTable === targetTable && !["reported", "fulfilled"].includes(request.status)
            ? { ...request, status: "blocked" }
            : request,
        ),
      );
      if (BACKEND_MODE && customerBackendApi && targetPartyId) {
        void customerBackendApi
          .createBlock({ targetPartyId })
          .catch((error) => toast(reportError(error, t("session.blockFailed"))));
      }
    },
    [toast],
  );

  const unblockTable = useCallback((targetTable: string) => {
    setBlockedTables((current) => current.filter((code) => code !== targetTable));
  }, []);

  const reportTable = useCallback(
    (targetTable: string, reason = "guest_report", targetPartyId?: string) => {
      const report: SafetyReport = {
        id: sequenceId("report"),
        tableCode: targetTable,
        reason,
        createdAt: nowIso(),
      };
      setReports((current) => [report, ...current]);
      setSentSocial((requests) => [
        {
          id: report.id,
          kind: "report",
          toTable: targetTable,
          subject: reason,
          status: "reported",
          createdAt: report.createdAt,
        },
        ...requests,
      ]);
      if (BACKEND_MODE && customerBackendApi && targetPartyId) {
        void customerBackendApi
          .createModerationReport({ targetPartyId, reason })
          .catch((error) => toast(reportError(error, t("session.reportFailed"))));
      }
    },
    [toast],
  );

  const respondGift = useCallback<SessionValue["respondGift"]>(
    async (giftId, action, senderPartyId) => {
      if (!(BACKEND_MODE && customerBackendApi)) return;
      try {
        if (action === "accept") {
          await customerBackendApi.acceptGift(giftId);
        } else {
          await customerBackendApi.rejectGift(giftId);
          if (action === "block" && senderPartyId) {
            await customerBackendApi.createBlock({ targetPartyId: senderPartyId });
            setBlockedTables((current) =>
              current.includes(senderPartyId) ? current : [...current, senderPartyId].sort(),
            );
          }
        }
      } catch (error) {
        toast(reportError(error, t("session.giftRespondFailed")));
        throw error;
      }
    },
    [toast],
  );

  const respondContact = useCallback<SessionValue["respondContact"]>(
    async (id, action) => {
      if (!(BACKEND_MODE && customerBackendApi)) return;
      try {
        if (action === "accept") await customerBackendApi.acceptContactRequest(id);
        else if (action === "reject") await customerBackendApi.rejectContactRequest(id);
        else await customerBackendApi.blockContactRequest(id);
      } catch (error) {
        toast(reportError(error, t("session.contactRespondFailed")));
        throw error;
      }
    },
    [toast],
  );

  const leaveVenue = useCallback(() => {
    if (order && !isOrderTerminal(order.status)) {
      return { ok: false, reason: t("guard.orderActive") };
    }
    if (BACKEND_MODE && customerBackendApi && checkIn) {
      void customerBackendApi
        .leaveCheckIn()
        .catch((error) => toast(reportError(error, t("session.leaveFailed"))));
    }
    cacheMode("private");
    setCheckIn(null);
    setCart([]);
    setOrder(null);
    setSentSocial([]);
    setTableCode("NO-TABLE");
    return { ok: true };
  }, [cacheMode, checkIn, order, toast]);

  const respondInbox = useCallback((id: string, action: InboxAction) => {
    setInbox((items) => {
      const item = items.find((candidate) => candidate.id === id);
      if (action === "block" && item) {
        setBlockedTables((current) =>
          current.includes(item.fromTable) ? current : [...current, item.fromTable].sort(),
        );
      }
      return applyInboxAction(items, id, action);
    });
  }, []);

  // Hydrate the active check-in on load so a reload returns checked-in with the
  // authoritative tonight mode / contact setting (backend mode only).
  useEffect(() => {
    if (!(BACKEND_MODE && customerBackendApi)) return;
    let cancelled = false;
    void customerBackendApi
      .getMyCheckIn()
      .then((summary) => {
        if (!cancelled && summary) applyCheckIn(summary);
      })
      .catch(() => {
        // A missing/failed check-in hydrate is non-fatal: the guest simply
        // re-joins. Avoid a noisy toast on every cold load.
      });
    return () => {
      cancelled = true;
    };
  }, [applyCheckIn]);

  /*
   * Mock staff auto-advance — visible controls can override this path. Disabled
   * in backend mode, where the live order screen polls GET /orders/me instead.
   */
  useEffect(() => {
    if (BACKEND_MODE) return;
    if (!order || isOrderTerminal(order.status)) return;
    const idx = ORDER_STEPS.findIndex((s) => s.id === order.status);
    if (idx < 0 || idx >= ORDER_STEPS.length - 1) return;
    const timer = setTimeout(
      () => {
        setOrder((current) => {
          if (!current || current.status !== order.status || isOrderTerminal(current.status))
            return current;
          const next = ORDER_STEPS[idx + 1];
          return next ? { ...current, status: next.id } : current;
        });
      },
      idx === 0 ? 2600 : 3600,
    );
    return () => clearTimeout(timer);
  }, [order]);

  const value = useMemo<SessionValue>(
    () => ({
      consent,
      acceptConsent,
      mode,
      setMode,
      tableCode,
      checkIn,
      joinTable,
      cart,
      cartCount: cartCount(cart),
      cartTotalMinor: cartTotalMinor(cart),
      addToCart,
      updateQty,
      removeFromCart,
      order,
      submitOrder,
      advanceOrderStatus,
      syncOrderStatus,
      settings,
      setAllowGifts,
      setAllowContact,
      canStartSocial,
      sentSocial,
      sendGiftRequest,
      sendContactRequest,
      blockedTables,
      reports,
      blockTable,
      unblockTable,
      reportTable,
      leaveVenue,
      inbox,
      pendingInbox: pendingCount(inbox),
      respondInbox,
      backendMode: BACKEND_MODE,
      respondGift,
      respondContact,
      toasts,
      toast,
    }),
    [
      consent,
      acceptConsent,
      mode,
      tableCode,
      checkIn,
      joinTable,
      cart,
      order,
      settings,
      setAllowGifts,
      setAllowContact,
      canStartSocial,
      sentSocial,
      sendGiftRequest,
      sendContactRequest,
      blockedTables,
      reports,
      blockTable,
      unblockTable,
      reportTable,
      leaveVenue,
      inbox,
      toasts,
      addToCart,
      updateQty,
      removeFromCart,
      submitOrder,
      advanceOrderStatus,
      syncOrderStatus,
      respondInbox,
      respondGift,
      respondContact,
      setMode,
      toast,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} />
    </SessionContext.Provider>
  );
}

function ToastViewport({ toasts }: { toasts: ToastMessage[] }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-28 z-50 flex flex-col items-center gap-2 px-5">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="glass-hi jod-fade-up flex items-center gap-2 rounded-lg px-4 py-3 font-medium text-cream text-sm shadow-[var(--shadow-pop)]"
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within a SessionProvider");
  return ctx;
}
